/**
 * Internal shared helpers of the semantics walker. Not part of the public
 * barrel — import from here only within the walker (and its specs).
 */

import {
  H5PParams,
  H5PSemanticsField,
  SemanticsResolver,
  SemanticsResolverFn,
  TranslatableFieldType,
  TranslationValue,
  TRANSLATABLE_CONDITION_SOURCE_NON_EMPTY,
  TRANSLATABLE_FIELD_TYPES,
} from './types';

/**
 * Libraries hard-excluded from translation: never extracted, never recursed
 * into, 0/0 in progress — even if semantics carried `translatable` flags.
 */
export const EXCLUDED_LIBRARIES: readonly string[] = [
  'H5P.DragQuestion',
  'H5P.DragQuestionSubactivity',
];

/**
 * Params paths of semantics groups the editor never renders: counting them
 * would put 100% out of reach. Suppresses COUNTING only — merge is unchanged.
 */
export const UNREACHABLE_FIELD_GROUP_PATHS: readonly (readonly string[])[] = [
  ['interactiveVideo', 'video', 'startScreenOptions'],
];

/**
 * Whether a field sits inside a group the editor never renders. Compared on
 * path segments, so it holds wherever the group appears in the tree.
 */
export const isUnreachableFieldPath = (
  pathSegments: ReadonlyArray<string | number>,
): boolean =>
  UNREACHABLE_FIELD_GROUP_PATHS.some(
    group =>
      pathSegments.length > group.length &&
      group.every((segment, index) => pathSegments[index] === segment),
  );

/**
 * Embedded libraries treated as per-language ASSETS when their `library` field
 * is translatable: the embed's whole `params` object is the unit.
 *
 * `H5P.Video` is deliberately absent: a video source is universal in every
 * module — translated video is delivered through per-language SUBTITLES.
 */
export const ASSET_EMBED_LIBRARIES: Readonly<
  Record<string, TranslatableFieldType>
> = {
  'H5P.Image': TRANSLATABLE_FIELD_TYPES.IMAGE,
  // File module: the Google Slides embed's params (the URL) are per-language
  // (classification sheet: "Embed Google Slides" = T, conditional).
  'H5P.EmbedGoogleSlides': TRANSLATABLE_FIELD_TYPES.MEDIA,
};

/**
 * Group widgets that keep H5PEditor.Group's rule of flattening a single-field
 * group. Custom widgets (wizard, slides, feedback, …) store a nested object.
 */
export const FLATTENING_GROUP_WIDGETS: readonly string[] = [
  'group',
  'questionSet',
];

/** Widget name of the custom overallFeedback ranges widget (quirk 2). */
export const FEEDBACK_GROUP_WIDGET = 'feedback';

/**
 * Params key the feedback widget stores its ranges under — always literally
 * `overallFeedback`, independent of the group's own name.
 */
export const FEEDBACK_RANGES_KEY = 'overallFeedback';

/** Field name of a feedback range's translatable string. */
export const FEEDBACK_RANGE_FIELD = 'feedback';

const TEXTUAL_FIELD_TYPES: readonly string[] = ['text', 'textarea', 'html'];
const HTML_WIDGET = 'html';
const IMAGE_MIME_PREFIX = 'image/';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Whether a value counts as content: non-blank string, non-empty array, or
 * object with at least one key.
 */
export const isNonEmptyValue = (value: unknown): value is TranslationValue => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (isRecord(value)) {
    return Object.keys(value).length > 0;
  }
  return false;
};

/**
 * Blank-CONTENT rule, shared verbatim with the module service's
 * `isEmptyTranslationValue` and the jQuery layer's `isEmptyValue`.
 */
export const isBlankContentValue = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    const text = value.replace(/<[^>]*>/g, '').trim();
    return text === '' && !value.includes('<img');
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (isRecord(value)) {
    return !hasDeepMediaContent(value);
  }
  return true;
};

// Keys under which an embed/media object stores its actual content; `source`
// is the Google Slides embed's URL, without which it would read as blank.
const CONTENT_KEYS = ['path', 'url', 'source'];

const hasDeepMediaContent = (value: Record<string, unknown>): boolean => {
  for (const [key, entry] of Object.entries(value)) {
    if (
      CONTENT_KEYS.includes(key) &&
      typeof entry === 'string' &&
      entry !== ''
    ) {
      return true;
    }
    if (typeof entry === 'object' && entry !== null) {
      if (
        Array.isArray(entry)
          ? entry.some(item => isRecord(item) && hasDeepMediaContent(item))
          : hasDeepMediaContent(entry as Record<string, unknown>)
      ) {
        return true;
      }
    }
  }
  return false;
};

/** Deep clone of JSON-ish data (params). Preserves `undefined` properties. */
export const deepCloneJson = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(entry => deepCloneJson(entry)) as unknown as T;
  }
  if (isRecord(value)) {
    const copy: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      copy[key] = deepCloneJson(value[key]);
    }
    return copy as unknown as T;
  }
  return value;
};

/** Normalize the resolver input into a function. */
export const toResolverFn = (
  resolver: SemanticsResolver | undefined,
): SemanticsResolverFn => {
  if (!resolver) {
    return () => undefined;
  }
  if (typeof resolver === 'function') {
    return resolver;
  }
  return (uberName: string) =>
    resolver[uberName] ?? resolver[machineNameOf(uberName)];
};

/** `'H5P.MultiChoice 1.14'` -> `'H5P.MultiChoice'`. */
export const machineNameOf = (uberName: string): string =>
  uberName.split(' ')[0];

/** Parsed shape of a `library`-field value in params. */
export interface LibraryValue {
  readonly uberName: string;
  readonly machineName: string;
  readonly params: Record<string, unknown> | undefined;
  readonly subContentId: string | undefined;
  readonly id: string | undefined;
}

export const parseLibraryValue = (value: unknown): LibraryValue | undefined => {
  if (!isRecord(value) || !isNonEmptyString(value['library'])) {
    return undefined;
  }
  const uberName = value['library'];
  return {
    uberName,
    machineName: machineNameOf(uberName),
    params: isRecord(value['params']) ? value['params'] : undefined,
    subContentId: isNonEmptyString(value['subContentId'])
      ? value['subContentId']
      : undefined,
    id: isNonEmptyString(value['id']) ? value['id'] : undefined,
  };
};

/** Stable key of a keyed item value: `subContentId ?? id`. */
export const stableKeyOf = (value: unknown): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  if (isNonEmptyString(value['subContentId'])) {
    return value['subContentId'];
  }
  if (isNonEmptyString(value['id'])) {
    return value['id'];
  }
  return undefined;
};

export const isGroupField = (field: H5PSemanticsField): boolean =>
  field.type === 'group';

export const isListField = (field: H5PSemanticsField): boolean =>
  field.type === 'list';

export const isLibraryField = (field: H5PSemanticsField): boolean =>
  field.type === 'library';

/** The overallFeedback ranges group (custom feedback widget, quirk 2). */
export const isFeedbackRangesGroup = (field: H5PSemanticsField): boolean =>
  isGroupField(field) && field.widget === FEEDBACK_GROUP_WIDGET;

/**
 * Whether a group's single child value is stored directly under the group's
 * name (H5PEditor.Group's rule for single-field, non-sub-content groups).
 */
export const isFlattenedGroup = (field: H5PSemanticsField): boolean =>
  isGroupField(field) &&
  (field.fields?.length ?? 0) === 1 &&
  field.isSubContent !== true &&
  (field.widget === undefined ||
    FLATTENING_GROUP_WIDGETS.includes(field.widget)) &&
  !isFeedbackRangesGroup(field);

export const isConditionalField = (field: H5PSemanticsField): boolean =>
  field.translatableCondition === TRANSLATABLE_CONDITION_SOURCE_NON_EMPTY;

/** Leaf types whose param value is an object (can carry a minted `id`). */
const RECORD_LEAF_TYPES: readonly string[] = [
  'image',
  'file',
  'video',
  'audio',
];

/**
 * Flattened list item whose single field is a record-valued leaf: the item IS
 * that object, so it can carry a minted `id`. Bare-string items cannot.
 */
export const isKeyableFlattenedItem = (itemDef: H5PSemanticsField): boolean => {
  const single = itemDef.fields?.[0];
  return (
    isFlattenedGroup(itemDef) &&
    !!single &&
    single.translatable === true &&
    typeof single.type === 'string' &&
    RECORD_LEAF_TYPES.includes(single.type)
  );
};

/**
 * Whether a `video`/`audio` value points at an EXTERNAL URL (absolute `path`)
 * rather than a local upload. Only external URLs are per-language.
 */
export const isExternalUrlMediaValue = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  const first = value[0];
  return (
    isRecord(first) &&
    typeof first['path'] === 'string' &&
    /^https?:\/\//i.test(first['path'])
  );
};

/** Whether a group directly contains a `translatable: true` field. */
export const hasDirectTranslatableField = (field: H5PSemanticsField): boolean =>
  (field.fields ?? []).some(child => child.translatable === true);

/**
 * Whether a group holds a translatable field below it through PLAIN groups
 * only — lists, libraries and sub-content key their own items.
 */
export const hasTranslatableFieldWithinGroups = (
  field: H5PSemanticsField,
): boolean =>
  (field.fields ?? []).some(
    child =>
      child.translatable === true ||
      (isGroupField(child) &&
        child.isSubContent !== true &&
        !isFeedbackRangesGroup(child) &&
        hasTranslatableFieldWithinGroups(child)),
  );

/**
 * Unit type of a translatable LEAF field, or `undefined` when the flag sits on
 * a type the walker does not support (the flag is then ignored).
 */
export const leafFieldType = (
  field: H5PSemanticsField,
  value: unknown,
): TranslatableFieldType | undefined => {
  if (field.type === undefined) {
    return undefined;
  }
  if (TEXTUAL_FIELD_TYPES.includes(field.type)) {
    return field.type === 'html' || field.widget === HTML_WIDGET
      ? TRANSLATABLE_FIELD_TYPES.HTML
      : TRANSLATABLE_FIELD_TYPES.TEXT;
  }
  if (field.type === 'image') {
    return TRANSLATABLE_FIELD_TYPES.IMAGE;
  }
  if (field.type === 'video' || field.type === 'audio') {
    return TRANSLATABLE_FIELD_TYPES.MEDIA;
  }
  if (field.type === 'file') {
    const mime = isRecord(value) ? value['mime'] : undefined;
    return typeof mime === 'string' && mime.startsWith(IMAGE_MIME_PREFIX)
      ? TRANSLATABLE_FIELD_TYPES.IMAGE
      : TRANSLATABLE_FIELD_TYPES.MEDIA;
  }
  return undefined;
};

/**
 * Asset type of a translatable `library` field with NO embed yet: defined only
 * when every allowed library in `options` is an asset library of one kind.
 */
export const assetTypeFromOptions = (
  field: H5PSemanticsField,
): TranslatableFieldType | undefined => {
  const options = (field.options ?? []).filter(
    (option): option is string => typeof option === 'string',
  );
  if (options.length === 0 || options.length !== (field.options ?? []).length) {
    return undefined;
  }
  const types = new Set(
    options.map(option => ASSET_EMBED_LIBRARIES[machineNameOf(option)]),
  );
  if (types.size !== 1) {
    return undefined;
  }
  const [only] = Array.from(types);
  return only;
};

/** Render path segments as a human-readable path string. */
export const pathToString = (
  segments: ReadonlyArray<string | number>,
): string =>
  segments.reduce<string>(
    (acc, segment) =>
      typeof segment === 'number'
        ? `${acc}[${segment}]`
        : acc.length > 0
          ? `${acc}/${segment}`
          : segment,
    '',
  );

/** Read a params path; `undefined` on any structural mismatch (never throws). */
export const getAtPath = (
  params: H5PParams,
  segments: ReadonlyArray<string | number>,
): unknown => {
  let current: unknown = params;
  for (const segment of segments) {
    if (typeof segment === 'number') {
      if (!Array.isArray(current)) {
        return undefined;
      }
      current = current[segment];
    } else {
      if (!isRecord(current)) {
        return undefined;
      }
      current = current[segment];
    }
  }
  return current;
};
